import { customerHasServiceDefinitions, getServiceLines } from './serviceCallback';
import { serviceCodeKey } from './serviceLines';

const clean = (value) => {
    if (value === null || value === undefined) return '';
    return `${value}`.trim();
};

const serviceLineCompanionType = (line = {}) => {
    return (
        clean(line.metadata?.requires_companion_type) ||
        clean(line.metadata?.required_companion_type) ||
        clean(line.requires_companion_type) ||
        clean(line.required_companion_type) ||
        ''
    );
};

const hasCompanionType = (lines = [], requiredType) => {
    const type = clean(requiredType).toLowerCase();
    return !!type && lines.some((line) => clean(line.type).toLowerCase() === type);
};

export const serviceLineRequiresCatalogDefinition = (line = {}, lines = []) => {
    const companionType = serviceLineCompanionType(line);
    if (companionType) return !hasCompanionType(lines, companionType);

    if (line.metadata?.catalog_required === false) return false;
    if (line.metadata?.catalog_managed === false) return false;
    if (line.catalog_required === false) return false;
    if (line.catalog_managed === false) return false;

    return true;
};

export const getOrderCatalogScope = (order) => {
    const scope = order?.service_catalog_filter;
    return scope && typeof scope === 'object' && !Array.isArray(scope) ? scope : {};
};

export const getServiceCatalogContext = (order, fallback = {}) => {
    const shipperId = order?.shipper_id || fallback.shipperId || fallback.shipper_id;
    if (!shipperId) return null;

    return {
        shipperId,
        orderScope: getOrderCatalogScope(order),
    };
};

const scopeKey = (scope) =>
    scope && typeof scope === 'object'
        ? Object.keys(scope)
              .sort()
              .map((key) => `${key}=${scope[key]}`)
              .join(',')
        : '';

export const serviceCatalogKey = ({ shipperId, orderScope } = {}) => [shipperId || '', scopeKey(orderScope)].join('|');

export const getServiceCatalogCacheStateForOrder = (order, catalogs = {}, errors = {}, fallback = {}) => {
    const context = getServiceCatalogContext(order, fallback);
    if (!context) {
        return { context: null, key: null, catalog: null, error: null, loading: false };
    }

    const key = serviceCatalogKey(context);
    const entry = catalogs[key];
    const catalog = entry?.catalog || entry || null;
    const error = errors?.[key] || null;
    return {
        context,
        key,
        catalog,
        error,
        loading: !catalog && !error,
    };
};

export const getServiceCatalogForOrder = (order, catalogs = {}, fallback = {}) => {
    return getServiceCatalogCacheStateForOrder(order, catalogs, {}, fallback).catalog;
};

export const getServiceCatalogServices = (serviceCatalog) =>
    Array.isArray(serviceCatalog?.services) ? serviceCatalog.services : [];

export const hasServiceCatalogDefinitions = (serviceCatalog) => getServiceCatalogServices(serviceCatalog).length > 0;

// "CODE - Description" for the service pickers. A code alone identifies nothing to anyone who has
// not memorised the catalog, and the descriptions are what users actually search by.
//
// A description that only repeats the code is dropped rather than rendered twice: the parser
// stamps description = code when the source order carries no human label, so "DEL - DEL" is a
// real shape in the data. Compared through serviceCodeKey so casing and padding do not defeat it.
export const serviceOptionLabel = (service) => {
    const code = `${service?.code ?? ''}`.trim();
    const description = `${service?.description ?? ''}`.trim();

    if (!code) return description;
    if (!description || serviceCodeKey(description) === serviceCodeKey(code)) return code;
    return `${code} - ${description}`;
};

// Integer-only: buildServiceLineFromCatalog rounds, so allowing fractions here would let a
// validated 1.5 save as 2.
export const canAddServiceLine = (selected, quantity) => {
    if (!selected || !selected.code) return false;
    const q = Number(quantity);
    if (!Number.isInteger(q) || q <= 0) return false;
    return true;
};

// Catalog matching uses the shared `serviceCodeKey` (trimmed + upper-cased) — the same key
// the display grouping and the group reducers use, so a code can't validate as one service
// here while rendering as two rows there.
export const getValidServiceCodesForOrder = (serviceCatalog) =>
    new Set(
        getServiceCatalogServices(serviceCatalog)
            .map((entry) => serviceCodeKey(entry.code))
            .filter(Boolean)
    );

export const getInvalidServiceLines = (order, serviceCatalog) => {
    if (!customerHasServiceDefinitions(order, serviceCatalog)) return [];
    const lines = getServiceLines(order);
    if (lines.length === 0 || !hasServiceCatalogDefinitions(serviceCatalog)) return [];
    const validCodes = getValidServiceCodesForOrder(serviceCatalog);
    return lines.filter((line) => {
        if (!serviceLineRequiresCatalogDefinition(line, lines)) return false;
        const code = serviceCodeKey(line.code);
        if (!code) return false;
        return !validCodes.has(code);
    });
};

// Descriptions live in the catalog, not on the line — the parser stamps description = code
// when the source order carries no human label — so backfill a group's description from the
// catalog when the line only echoes its code. Display-only; the stored line is untouched.
// A catalog entry that just echoes the code is no label either, so it's left alone.
export const applyCatalogDescriptions = (groups = [], serviceCatalog) => {
    const byCode = new Map(
        getServiceCatalogServices(serviceCatalog).map((option) => [serviceCodeKey(option.code), option])
    );
    return groups.map((group) => {
        if (group.description && group.description !== group.code) return group;
        const description = byCode.get(serviceCodeKey(group.code))?.description;
        return description && serviceCodeKey(description) !== serviceCodeKey(group.code)
            ? { ...group, description }
            : group;
    });
};

export const getMissingServiceCatalogFields = (order, catalogs = {}, errors = {}, fallback = {}) => {
    const { context, catalog } = getServiceCatalogCacheStateForOrder(order, catalogs, errors, fallback);
    if (!context) return [];
    if (!customerHasServiceDefinitions(order, catalog)) return [];
    return catalog ? [] : ['services'];
};

export const buildServiceLineFromCatalog = (selected, quantity) => {
    const parsed = Math.round(Number(quantity));
    const q = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    return {
        code: selected.code,
        description: selected.description || selected.code,
        type: selected.type || 'unknown',
        ...(selected.size ? { size: selected.size } : {}),
        // Persist the catalog's require_confirm onto the order's service so the
        // mobile app reads it off the order (not the catalog).
        require_confirm: !!selected.require_confirm,
        // Snapshot the catalog's billable flag so a warranty/no-charge service isn't billed
        // just because the DB column defaults to true.
        billable: selected.billable !== false,
        // Snapshot the catalog's service_time onto the line (0 is meaningful).
        ...(Number.isFinite(selected.service_time) ? { service_time: selected.service_time } : {}),
        quantity: q,
    };
};
